import { z } from 'zod'
import { OPERATION_BY_ID } from './image-operations'

/**
 * What the AI features have cost — the KI-Nutzung panel under Einstellungen.
 *
 * ONE FIGURE PER CALL, and it already includes the markup. The purchase price,
 * the markup percentage, the provider and the TOKEN COUNTS are deliberately
 * ABSENT from this contract — not hidden in the interface, absent. Tokens are
 * on the list for a sharper reason than the rest: the model is visible, its list
 * price is public, and tokens times list price IS the purchase price. A test
 * walks every key of the report and fails if one of them appears.
 *
 * Money crosses this boundary as an INTEGER of micro-dollars (USD x 1e6). A
 * float that is a hundredth of a cent out per row becomes a total nobody can
 * explain. It becomes a string exactly once, in `formatUsd`.
 */

export const AI_FEATURES = ['image', 'briefing'] as const
export const aiFeatureSchema = z.enum(AI_FEATURES)
export type AiFeature = z.infer<typeof aiFeatureSchema>

export const AI_FEATURE_LABELS: Record<AiFeature, string> = {
  image: 'Bild-KI',
  briefing: 'Briefings',
}

export const aiUsageStatusSchema = z.enum(['succeeded', 'failed'])
export type AiUsageStatus = z.infer<typeof aiUsageStatusSchema>

/** `2026-10`, a calendar month in the reporting timezone. */
export const AI_USAGE_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/
export const aiUsageMonthSchema = z.string().regex(AI_USAGE_MONTH_PATTERN, 'Monat als JJJJ-MM')

/** The size measure the agency may see for an image edit: which edit it was. */
export const imageUsageScopeSchema = z.object({
  /** A string, not the operation enum: a row outlives a retired operation. */
  operation: z.string(),
})

/** For a briefing: how much went in, in units a person can picture. Never tokens. */
export const briefingUsageScopeSchema = z.object({
  documents: z.number().int().nonnegative(),
  pages: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  /**
   * `ocr` on the row of a briefing's TEXT RECOGNITION call (Mistral OCR on the
   * scanned pages), billed apart from the model call. Then only `pages` means
   * anything: the pages read. Absent on the model call's own row.
   */
  stage: z.literal('ocr').optional(),
})

const rowBase = {
  id: z.string(),
  /** When the call finished. */
  at: z.iso.datetime(),
  /** Null for a call that belonged to no object. A pruned object keeps its id. */
  estate: z
    .object({
      id: z.number().int(),
      externalNumber: z.string().nullable(),
      title: z.string().nullable(),
    })
    .nullable(),
  /** Null when nobody was recorded, or the account has since been deleted. */
  user: z.object({ name: z.string() }).nullable(),
  /** Includes the markup — the only money field there is. */
  billedMicroUsd: z.number().int().nonnegative(),
  status: aiUsageStatusSchema,
}

export const aiUsageReportRowSchema = z.discriminatedUnion('feature', [
  z.object({ ...rowBase, feature: z.literal('image'), scope: imageUsageScopeSchema }),
  z.object({ ...rowBase, feature: z.literal('briefing'), scope: briefingUsageScopeSchema }),
])

export type AiUsageReportRow = z.infer<typeof aiUsageReportRowSchema>

export const aiUsageReportSchema = z.object({
  month: aiUsageMonthSchema,
  /** The timezone the month and its days were cut in — printed, so it is never a guess. */
  timeZone: z.string(),
  /** The limit in billed micro-dollars — for the CURRENT month only; null for a past month or no limit. */
  limitMicroUsd: z.number().int().positive().nullable(),

  totals: z.object({
    calls: z.number().int().nonnegative(),
    billedMicroUsd: z.number().int().nonnegative(),
  }),

  /** Every feature, always — a zero is an answer, a missing tile is a question. */
  byFeature: z.array(
    z.object({
      feature: aiFeatureSchema,
      calls: z.number().int().nonnegative(),
      billedMicroUsd: z.number().int().nonnegative(),
    }),
  ),

  /** Every day of the month, quiet ones included, so the bars line up with the calendar. */
  series: z.array(
    z.object({
      /** `2026-10-15`, local to `timeZone`. */
      day: z.string(),
      calls: z.number().int().nonnegative(),
      billedMicroUsd: z.number().int().nonnegative(),
    }),
  ),

  /** Newest first. */
  rows: z.array(aiUsageReportRowSchema),
})

export type AiUsageReport = z.infer<typeof aiUsageReportSchema>

/**
 * Micro-dollars to "2,41 $". The ONLY place money becomes a string.
 *
 * `symbol: false` gives "2,41" — what goes into an input field, where the
 * currency is the field's label and a typed "$" would only be parsed away.
 */
export function formatUsd(
  microUsd: number,
  fractionDigits = 2,
  { symbol = true }: { symbol?: boolean } = {},
): string {
  return new Intl.NumberFormat('de-DE', {
    ...(symbol ? { style: 'currency', currency: 'USD' } : { useGrouping: false }),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(microUsd / 1_000_000)
}

/**
 * The "Umfang" column, worded once for the panel and the CSV alike, so the
 * spreadsheet attached to an invoice says what the screen said.
 */
export type AiUsageScope =
  | { feature: 'image'; scope: z.infer<typeof imageUsageScopeSchema> }
  | { feature: 'briefing'; scope: z.infer<typeof briefingUsageScopeSchema> }

export function describeUsageScope(row: AiUsageScope): string {
  if (row.feature === 'image') {
    const spec = OPERATION_BY_ID.get(row.scope.operation as never)
    if (!spec) return row.scope.operation
    // Innen and Außen share some labels ("Personen entfernen") on purpose.
    return `${spec.label} (${spec.scene === 'interior' ? 'innen' : 'außen'})`
  }

  const { documents, pages, bytes } = row.scope
  if (row.scope.stage === 'ocr') {
    // Documents and megabytes belong to the model call's row; repeating them
    // here would count the same documents twice in the panel.
    return `Texterkennung · ${pages} ${pages === 1 ? 'Seite' : 'Seiten'}`
  }
  const megabytes = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(
    bytes / 1_000_000,
  )
  return `${documents} ${documents === 1 ? 'Dokument' : 'Dokumente'} · ${pages} ${
    pages === 1 ? 'Seite' : 'Seiten'
  } · ${megabytes} MB`
}
